import React, { useState, useEffect, useContext } from "react"
import { Link } from "react-router-dom"
import { Flex, Text, Button } from "@capioweb/ui-components"
import { LocalesContext } from "../../../context"
import { CareerFilterContext } from "../context/CareerFilterContext"
import { urlIsInternal } from "../../../utils"
import { CareerItem } from "./CareerItem"
import { sendJobAdDataLayerPush } from "../../../utils/piwikPro"

interface CareerListProps {
	items: CareerItem[]
	showAvailableJobCountText: boolean
}

export const CareerList: React.FC<CareerListProps> = ({
	items,
	showAvailableJobCountText = false,
}) => {
	const { translate, translationKeys } = React.useContext(LocalesContext)
	const { activeCountyIds, activeJobTypeIds, activeEmploymentTypeIds } =
		useContext(CareerFilterContext)
	const [activeItems, setActiveItems] = useState(items)
	const [showAll, setShowAll] = useState(false)
	const initialItemLimit = 12

	useEffect(() => {
		let filteredItems = items

		if (activeCountyIds.length > 0) {
			filteredItems = filteredItems.filter((item) =>
				activeCountyIds.includes(item.countyId)
			)
		}

		if (activeJobTypeIds.length > 0) {
			filteredItems = filteredItems.filter((item) =>
				activeJobTypeIds.includes(item.jobTypeId)
			)
		}
		if (activeEmploymentTypeIds.length > 0) {
			filteredItems = filteredItems.filter((item) =>
				activeEmploymentTypeIds.includes(item.employmentTypeId)
			)
		}

		setActiveItems(filteredItems)
	}, [activeCountyIds, activeJobTypeIds, activeEmploymentTypeIds])

	const resultCountText = showAvailableJobCountText
		? translate(translationKeys.careerFilterResultCount)
		: translate(translationKeys.careerSpontaniousFilterResultCount)

	return (
		<>
			<Flex w={1} justifyContent="center" mb={{ _: 3, m: 4 }}>
				<Text mb={0} textAlign="center">
					{resultCountText.replace(/%s/g, activeItems.length.toString())}:
				</Text>
			</Flex>

			<Flex w={1} columnGap={{ m: 3 }} rowGap={{ _: 2, m: 3 }} flexWrap="wrap">
				{activeItems.map((item, i) => {
					return (
						<Flex
							w={1}
							flexGrow={0}
							flexShrink={1}
							flexBasis={{
								_: "100%",
								m: "calc(100% / 2 - 12px)",
								l: "calc(100% / 3 - 16px)",
							}}
							key={i}
							display={!showAll && i >= initialItemLimit ? "none" : "flex"}
						>
							{urlIsInternal(item.url) ? (
								<Link
									onClick={() =>
										sendJobAdDataLayerPush("view_ad", item.displayName)
									}
									to={item?.url ?? "#"}
									style={{ width: "100%", height: "100%" }}
								>
									<CareerItem
										displayName={item.displayName}
										county={item.county}
										unit={item.unit}
										applyByDate={item.applyByDate}
									/>
								</Link>
							) : (
								<a
									onClick={() =>
										sendJobAdDataLayerPush("view_ad", item.displayName)
									}
									href={item.url}
									style={{ width: "100%", height: "100%" }}
								>
									<CareerItem
										displayName={item.displayName}
										county={item.county}
										unit={item.unit}
										applyByDate={item.applyByDate}
									/>
								</a>
							)}
						</Flex>
					)
				})}
			</Flex>
			{!showAll && activeItems.length > initialItemLimit && (
				<Button
					mt={{ _: 2, m: 3 }}
					variant="ghost"
					icon="plus"
					textColor="navy"
					onClick={() => setShowAll(true)}
				>
					{translate(translationKeys.showMore)}
				</Button>
			)}
		</>
	)
}
